<script lang="ts">
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { dev } from "$app/environment";
	import { goto } from "$app/navigation";
	import { page } from "$app/state";
	import { locale } from "svelte-i18n";
	import { userStore } from "$lib/stores/user";
	import { api } from "$lib/services/api";
	import { syncFromServer, syncToServer } from "$lib/services/sync";
	import {
		initPostHog,
		identifyUser,
		resetUser,
	} from "$lib/services/analytics";
	import {
		initI18n,
		applyLocaleToDocument,
		getSavedLocale,
		type SupportedLocale,
	} from "$lib/i18n";
	import ReminderBanner from "$lib/components/ReminderBanner.svelte";
	import AdminBanner from "$lib/components/AdminBanner.svelte";
	import CookieBanner from "$lib/components/CookieBanner.svelte";
	import AppShell from "$lib/components/AppShell.svelte";
	import "../app.css";

	let { children } = $props();

	$effect(() => {
		const lang = $locale as SupportedLocale | null;
		if (lang) applyLocaleToDocument(lang);
	});

	onMount(() => {
		initI18n();
		applyLocaleToDocument(getSavedLocale() as SupportedLocale);

		if (!dev) {
			// Defer PostHog off the critical path — it's a dynamic import now,
			// so this also keeps it out of the shared layout chunk entirely.
			if (typeof requestIdleCallback === "function") {
				requestIdleCallback(() => initPostHog());
			} else {
				setTimeout(initPostHog, 1);
			}
		}
		if ("serviceWorker" in navigator) {
			void import("virtual:pwa-register").then(({ registerSW }) => {
				registerSW({ immediate: true });
			});
		}
		userStore.init();
		if (!isPublicRoute) {
			// Dynamic import: keeps dexie (IndexedDB) out of the bundle that
			// anonymous marketing-page visitors download — they never need it.
			void import("$lib/services/scripts").then(({ refreshScripts }) =>
				refreshScripts().catch(() => {
					// offline or scripts unavailable — keep existing cache
				}),
			);
		}

		let wasAuthenticated = false;

		const unsubscribe = userStore.subscribe((state) => {
			if (state.isAuthenticated && !wasAuthenticated) {
				syncFromServer();
				if (state.user) {
					identifyUser(state.user.id, { email: state.user.email });
				}
				// Fetch profile to apply the user's saved theme preference globally
				api.user
					.getProfile()
					.then((profile) => {
						if (
							profile.theme &&
							["light", "dark", "system"].includes(profile.theme)
						) {
							localStorage.setItem("grafem-theme", profile.theme);
							const root = document.documentElement;
							if (profile.theme === "light")
								root.style.colorScheme = "light";
							else if (profile.theme === "dark")
								root.style.colorScheme = "dark";
							else root.style.removeProperty("color-scheme");
						}
					})
					.catch(() => {
						// offline or API unavailable — leave current theme as-is
					});
				const pathname = page.url.pathname;
				if (
					pathname !== "/onboarding" &&
					!pathname.startsWith("/auth")
				) {
					if (state.user?.role === "user") {
						// Only plain users need the onboarding check
						api.onboarding
							.status()
							.then(({ onboarded }) => {
								if (!onboarded) goto("/onboarding");
							})
							.catch(() => {
								// offline or API unavailable — stay on current page
							});
					}
				}
			} else if (!state.isAuthenticated && wasAuthenticated) {
				resetUser();
			}
			wasAuthenticated = state.isAuthenticated;
		});

		const syncInterval = setInterval(
			() => {
				const state = $userStore;
				if (state.isAuthenticated) {
					syncToServer();
				}
			},
			5 * 60 * 1000,
		);

		return () => {
			clearInterval(syncInterval);
			unsubscribe();
		};
	});

	const isPublicRoute = $derived(
		page.url.pathname === "/" ||
			page.url.pathname.startsWith("/legal") ||
			page.url.pathname.startsWith("/writing-systems") ||
			page.url.pathname.startsWith("/auth"),
	);
	const noShell = $derived(
		isPublicRoute ||
			page.url.pathname.startsWith("/learn") ||
			page.url.pathname.startsWith("/onboarding"),
	);
</script>

<svelte:head>
	{@html '<script>(function(){var t=localStorage.getItem("grafem-theme");if(t==="light")document.documentElement.style.colorScheme="light";else if(t==="dark")document.documentElement.style.colorScheme="dark";var l=localStorage.getItem("grafem-language")||"en";document.documentElement.lang=l;document.documentElement.dir=l==="ar"?"rtl":"ltr";})()</script>'}
</svelte:head>

<div
	class="min-h-screen flex flex-col font-primary bg-(--background) text-(--foreground)"
>
	{#if import.meta.env.DEV}
		<AdminBanner />
	{/if}
	<ReminderBanner />
	{#if noShell}
		{#key page.url.pathname}
			<div in:fade={{ duration: 150 }}>
				{@render children()}
			</div>
		{/key}
	{:else}
		<AppShell>
			{#key page.url.pathname}
				<div class="flex flex-1 flex-col" in:fade={{ duration: 150 }}>
					{@render children()}
				</div>
			{/key}
		</AppShell>
	{/if}
</div>
{#if !dev}
	<CookieBanner />
{/if}
